import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Save } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  DENTAL CHART PAGE — FDI-based tooth grid + per-tooth history.
//  Upper arch: 18-11 | 21-28
//  Lower arch: 48-41 | 31-38
// ═══════════════════════════════════════════════════════════════════

const STATUS_COLORS = {
  healthy:        '#f3f4f6',
  caries:         '#fecaca',
  filled:         '#a5b4fc',
  crown:          '#fde68a',
  bridge:         '#fcd34d',
  rct:            '#c4b5fd',
  extracted:      '#6b7280',
  missing:        '#374151',
  implant:        '#06b6d4',
  impacted:       '#ec4899',
  mobile:         '#f59e0b',
  veneer:         '#93c5fd',
  fracture:       '#ef4444',
  supernumerary:  '#a3e635',
  erupting:       '#84cc16',
};

const STATUS_LABELS = Object.keys(STATUS_COLORS);

const UPPER_RIGHT = ['18','17','16','15','14','13','12','11'];
const UPPER_LEFT  = ['21','22','23','24','25','26','27','28'];
const LOWER_LEFT  = ['31','32','33','34','35','36','37','38'];
const LOWER_RIGHT = ['48','47','46','45','44','43','42','41'];

const DentalChartPage = ({ API_BASE }) => {
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState('');
  const [patientId, setPatientId] = useState('');
  const [chart, setChart] = useState(null);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/patients`).then(r => setPatients(r.data || []));
  }, [API_BASE]);

  const loadChart = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/dental-chart/${patientId}`);
      setChart(r.data);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, patientId]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const loadHistory = async (toothNumber) => {
    try {
      const r = await axios.get(`${API_BASE}/dental-chart/${patientId}/history/${toothNumber}`);
      setHistory(r.data || []);
    } catch { setHistory([]); }
  };

  const clickTooth = (toothNumber) => {
    const tooth = chart?.teeth.find(t => t.toothNumber === toothNumber);
    setSelectedTooth(tooth);
    if (tooth) loadHistory(toothNumber);
  };

  const updateTooth = async (updates) => {
    if (!selectedTooth) return;
    try {
      const r = await axios.put(`${API_BASE}/dental-chart/${patientId}/tooth/${selectedTooth.toothNumber}`, { ...updates, updatedBy: 'admin' });
      // Update in place
      setChart(c => ({ ...c, teeth: c.teeth.map(t => t.toothNumber === selectedTooth.toothNumber ? r.data : t) }));
      setSelectedTooth(r.data);
    } catch (e) { alert('Update failed: ' + (e.response?.data?.error || e.message)); }
  };

  const filteredPatients = patients.filter(p =>
    !query || p.name?.toLowerCase().includes(query.toLowerCase()) || p.phone?.includes(query) || p.uhid?.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 20);

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 16px' }}>🦷 Dental Chart</h2>

      {/* Patient selector */}
      <div style={{ ...cardStyle, marginBottom:12, display:'flex', gap:10, alignItems:'center' }}>
        <Search size={18} color="#888"/>
        <input placeholder="Search patient by name/phone/UHID"
          value={query} onChange={e => setQuery(e.target.value)}
          style={{ ...inputStyle, flex:1 }}/>
        <select value={patientId} onChange={e => setPatientId(e.target.value)} style={{ ...inputStyle, width:320 }}>
          <option value="">Select patient…</option>
          {filteredPatients.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone}) {p.uhid ? `· ${p.uhid}` : ''}</option>)}
        </select>
      </div>

      {loading && <div style={{ textAlign:'center', color:'#888', padding:40 }}>Loading chart…</div>}

      {chart && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:16 }}>
          {/* Chart grid */}
          <div style={cardStyle}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontWeight:700, color: theme.navy }}>{chart.patientName}</div>
              <div style={{ fontSize:12, color:'#666' }}>{chart.dentition} dentition</div>
            </div>

            {/* Upper arch */}
            <div style={{ textAlign:'center', fontSize:11, color:'#888', marginBottom:4 }}>UPPER</div>
            <div style={{ display:'flex', justifyContent:'center', gap:2, marginBottom:6 }}>
              {UPPER_RIGHT.map(t => <ToothBtn key={t} num={t} chart={chart} selected={selectedTooth?.toothNumber === t} onClick={clickTooth}/>)}
              <div style={{ width:8 }}/>
              {UPPER_LEFT.map(t => <ToothBtn key={t} num={t} chart={chart} selected={selectedTooth?.toothNumber === t} onClick={clickTooth}/>)}
            </div>
            <div style={{ textAlign:'center', fontSize:11, color:'#888', margin:'6px 0' }}>─── Midline ───</div>
            {/* Lower arch */}
            <div style={{ display:'flex', justifyContent:'center', gap:2 }}>
              {LOWER_RIGHT.map(t => <ToothBtn key={t} num={t} chart={chart} selected={selectedTooth?.toothNumber === t} onClick={clickTooth}/>)}
              <div style={{ width:8 }}/>
              {LOWER_LEFT.map(t => <ToothBtn key={t} num={t} chart={chart} selected={selectedTooth?.toothNumber === t} onClick={clickTooth}/>)}
            </div>
            <div style={{ textAlign:'center', fontSize:11, color:'#888', marginTop:4 }}>LOWER</div>

            {/* Legend */}
            <div style={{ marginTop:16, padding:10, background:'#f9fafb', borderRadius:8 }}>
              <div style={{ fontSize:11, color:'#666', fontWeight:600, marginBottom:6, letterSpacing:1 }}>LEGEND</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {STATUS_LABELS.map(s => (
                  <div key={s} style={{ display:'flex', gap:4, alignItems:'center', fontSize:11 }}>
                    <div style={{ width:14, height:14, background: STATUS_COLORS[s], borderRadius:3, border:'1px solid #ccc' }}/>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tooth detail panel */}
          <div style={cardStyle}>
            {!selectedTooth ? (
              <div style={{ padding:40, textAlign:'center', color:'#888' }}>
                Click a tooth to view / edit its status
              </div>
            ) : (
              <>
                <div style={{ fontSize:24, fontWeight:800, color: theme.navy, marginBottom:4 }}>
                  Tooth #{selectedTooth.toothNumber}
                </div>
                <div style={{ fontSize:12, color:'#666', marginBottom:12 }}>
                  Last updated: {selectedTooth.updatedAt ? new Date(selectedTooth.updatedAt).toLocaleDateString('en-IN') : '—'}
                </div>

                <label style={lbl}>Status</label>
                <select value={selectedTooth.status} onChange={e => updateTooth({ status: e.target.value })} style={{ ...inputStyle, marginBottom:10 }}>
                  {STATUS_LABELS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <label style={lbl}>Surface Conditions</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
                  {['M','D','O','B','L','I'].map(srf => {
                    const existing = selectedTooth.surfaces?.find(s => s.surface === srf);
                    return (
                      <input key={srf}
                        placeholder={`${srf} condition`}
                        value={existing?.condition || ''}
                        onChange={e => {
                          const surfaces = (selectedTooth.surfaces || []).filter(s => s.surface !== srf);
                          if (e.target.value) surfaces.push({ surface: srf, condition: e.target.value, notedAt: new Date() });
                          updateTooth({ surfaces });
                        }}
                        style={{ ...inputStyle, fontSize:12 }}/>
                    );
                  })}
                </div>

                <label style={lbl}>Notes</label>
                <textarea value={selectedTooth.notes || ''} onChange={e => updateTooth({ notes: e.target.value })}
                  style={{ ...inputStyle, minHeight:60, marginBottom:12 }}/>

                {/* Treatment history for this tooth */}
                <div style={{ marginTop:16, paddingTop:12, borderTop:'1px solid #f1f3f5' }}>
                  <div style={{ fontWeight:700, color: theme.navy, marginBottom:8 }}>
                    Treatment History ({history.length})
                  </div>
                  {history.length === 0 ? <div style={{ fontSize:12, color:'#888' }}>No treatments recorded for this tooth</div>
                   : history.map(h => (
                    <div key={h._id} style={{ padding:8, background:'#f9fafb', borderRadius:6, marginBottom:6, fontSize:12 }}>
                      <div style={{ fontWeight:600, color: theme.navy }}>{h.treatmentName}</div>
                      <div style={{ color:'#666' }}>
                        {h.status === 'done' ? `✓ ${new Date(h.doneDate).toLocaleDateString('en-IN')}` : `○ planned ${new Date(h.plannedDate).toLocaleDateString('en-IN')}`}
                        {' · ₹'}{h.totalAmount || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ToothBtn = ({ num, chart, selected, onClick }) => {
  const tooth = chart.teeth.find(t => t.toothNumber === num);
  const color = tooth?.color || STATUS_COLORS[tooth?.status || 'healthy'];
  return (
    <button onClick={() => onClick(num)}
      style={{
        width:38, height:50, padding:0,
        background: color, border: selected ? `2px solid ${theme.primary}` : '1px solid #ccc',
        borderRadius:4, cursor:'pointer',
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        fontSize:10, color: tooth?.status === 'missing' || tooth?.status === 'extracted' ? 'white' : '#111',
        fontWeight: selected ? 700 : 500,
      }}
      title={`${num} · ${tooth?.status || '—'}${tooth?.notes ? ' · ' + tooth.notes : ''}`}>
      <span style={{ fontSize:11, fontWeight:700 }}>{num}</span>
      {tooth?.status !== 'healthy' && tooth?.status && (
        <span style={{ fontSize:8, marginTop:2, opacity:0.8 }}>{tooth.status.slice(0, 4)}</span>
      )}
    </button>
  );
};

const lbl = { fontSize:12, color:'#555', fontWeight:600, marginBottom:4, display:'block' };

export default DentalChartPage;
